import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  DatePicker,
  Dropdown,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Upload,
} from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  UploadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import * as dayjs from "dayjs";
import { useFirestore } from "react-redux-firebase";
import { useTranslation } from "react-i18next";
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";

import {
  FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE,
  FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { formatPriceByCurrency } from "../../../../../functions/formatters";
import {
  getOriginalQuantityForLine,
  getOriginalUnitPriceForLine,
} from "../../../../../functions/purchaseInvoiceLines";
import { findDuplicatePaymentMatches } from "../../../../../functions/duplicatePayments";
import { findProformaLinkIssues } from "../../../../../functions/proformaLinks";
import { getInvoiceDeletionBlockers } from "../../../../../functions/invoiceDeletion";
import { deleteInvoice, InvoiceDeletionBlockedError } from "../../../../../functions/firestore/invoiceDeletion";
import {
  ProformaLinkConflictError,
  recomputeFinalInvoiceTotals as recomputeFinalInvoiceTotalsFor,
  saveProformaLinks,
} from "../../../../../functions/firestore/proformaLinks";
import urls from "../../../../../urls";
import { useSelector } from "react-redux";
import {
  useProducts,
  usePurchaseOrderInvoice,
  usePurchaseOrderInvoices,
  usePurchaseOrderPayments,
  usePurchaseOrders,
  usePurchaseOrderShipments,
  useSuppliers,
} from "../../../../../data/hooks";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import ExcelExport from "../../../../../components/shared-components/ExcelExport";
import { getPurchaseOrderInvoiceExcelExportData } from "../../../../../functions/excel";
import {
  DetailLayout,
  KeyValueList,
  Num,
  PageHeader,
  RecordLink,
  SectionCard,
} from "../../../../../components/shared-components/purchase";

const { TextArea } = Input;
const { Panel } = Collapse;

const invoiceStatusColors = {
  draft: "default",
  submitted: "processing",
  approved: "blue",
  partially_paid: "orange",
  paid: "green",
  disputed: "red",
};

const invoiceStatusLabels = {
  draft: "Concept",
  submitted: "Ingediend",
  approved: "Goedgekeurd",
  partially_paid: "Deels betaald",
  paid: "Betaald",
  disputed: "Betwist",
};

const PurchaseOrderInvoiceDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const [saving, setSaving] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [paymentDate, setPaymentDate] = useState(dayjs());
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [uploadingPaymentProof, setUploadingPaymentProof] = useState(false);
  const [paymentExchangeRate, setPaymentExchangeRate] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [savingCorrection, setSavingCorrection] = useState(false);
  const [correctionAmount, setCorrectionAmount] = useState(null);
  const [correctionIsNegative, setCorrectionIsNegative] = useState(false);
  const [correctionDate, setCorrectionDate] = useState(dayjs());
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionReference, setCorrectionReference] = useState("");
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [editingInvoiceNumber, setEditingInvoiceNumber] = useState(false);
  const [invoiceNumberValue, setInvoiceNumberValue] = useState("");
  const [savingInvoiceNumber, setSavingInvoiceNumber] = useState(false);
  const [editingInvoiceDate, setEditingInvoiceDate] = useState(false);
  const [invoiceDateValue, setInvoiceDateValue] = useState(null);
  const [savingInvoiceDate, setSavingInvoiceDate] = useState(false);
  const [editingTotalAmount, setEditingTotalAmount] = useState(false);
  const [totalAmountValue, setTotalAmountValue] = useState(null);
  const [savingTotalAmount, setSavingTotalAmount] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [uploadingInvoicePdf, setUploadingInvoicePdf] = useState(false);
  const [editedInvoiceLines, setEditedInvoiceLines] = useState([]);
  const [editedAdditionalCosts, setEditedAdditionalCosts] = useState([]);
  const [editedDiscountLines, setEditedDiscountLines] = useState([]);
  const [editedLinkedProformaIds, setEditedLinkedProformaIds] = useState([]);
  const [editingProformaLinks, setEditingProformaLinks] = useState(false);
  const [savingProformaLinks, setSavingProformaLinks] = useState(false);
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newLineQuantity, setNewLineQuantity] = useState(1);
  const [newLineUnitPrice, setNewLineUnitPrice] = useState(0);
  const [newLineDiscount, setNewLineDiscount] = useState(0);
  const [legacyPayments, setLegacyPayments] = useState([]);
  const [syncingToExact, setSyncingToExact] = useState(false);
  const email = useSelector((state) => state.firebaseReducer.auth.email);
  const uid = useSelector((state) => state.firebaseReducer.auth.uid);
  // Verwijderen (zie handleDeleteInvoice)
  const deletedRef = useRef(false);
  const [deletingInvoice, setDeletingInvoice] = useState(false);

  // Use hooks for invoices, shipments, payments, and purchase orders
  const { invoices: allInvoices } = usePurchaseOrderInvoices();
  const { invoice, loading: invoiceLoading, invoiceHistory, historyLoading } = usePurchaseOrderInvoice(id);
  const { shipments: allShipments } = usePurchaseOrderShipments();
  const { payments } = usePurchaseOrderPayments({ invoiceId: id });
  const { purchaseOrders } = usePurchaseOrders();
  const { suppliers } = useSuppliers();
  const { payments: rawLinkedProformaPayments } = usePurchaseOrderPayments({
    invoiceIds: invoice?.linkedProformaInvoiceIds || [],
  });

  // Every invoice of this supplier (incl. this one), for the duplicate-payment check below
  const supplierInvoiceIds = allInvoices.filter((inv) => inv.supplierId === invoice?.supplierId).map((inv) => inv.id);
  const { payments: supplierPayments } = usePurchaseOrderPayments({ invoiceIds: supplierInvoiceIds });
  const invoicesById = useMemo(() => new Map(allInvoices.map((inv) => [inv.id, inv])), [allInvoices]);

  // Possible duplicates of the payment currently being entered/edited in the modal, against
  // this supplier's other payments (excluding the payment being edited itself)
  const duplicatePaymentMatches =
    paymentAmount && paymentDate
      ? findDuplicatePaymentMatches(
          { id: editingPayment?.id || null, amount: paymentAmount, paymentDate: paymentDate.unix() },
          supplierPayments,
          invoicesById,
        )
      : [];

  // Find the shipment by invoice.shipmentId
  const shipment = allShipments.find((ship) => ship.id === invoice?.shipmentId);
  const order = purchaseOrders.find((order) => order.id === invoice?.purchaseOrderId);

  // Get current supplier to extract productIds
  const currentSupplier = suppliers.find((s) => s.uid === shipment?.supplierId);
  const { products } = useProducts({ productIds: currentSupplier?.productIds || [] });

  // Proforma <-> final invoice linking
  const linkedProformaInvoices = (invoice?.linkedProformaInvoiceIds || [])
    .map((pid) => allInvoices.find((inv) => inv.id === pid))
    .filter(Boolean);
  const linkedFinalInvoice =
    invoice?.type === "proforma" && invoice?.linkedFinalInvoiceId
      ? allInvoices.find((inv) => inv.id === invoice.linkedFinalInvoiceId)
      : null;
  // Candidate proforma invoices to link: any proforma of this supplier that isn't already
  // settled against a (different) final invoice. Scoping this to just the shipment's own
  // source orders (as before) hid proformas for this supplier's other orders that never
  // ended up in this particular shipment - a deposit doesn't have to travel with the same
  // shipment as its order to still need settling against a final invoice.
  //
  // Wat al in de lijst van deze factuur staat, staat er ook altijd tussen, ook als de pro forma
  // volgens zichzelf elders verrekend is. Een waarde zonder eigen checkbox gooit antd's
  // Checkbox.Group bij de eerstvolgende klik stil weg, en dat ontkoppelde hem dan ongewild.
  const candidateProformaInvoices = allInvoices
    .filter(
      (inv) =>
        inv.type === "proforma" &&
        inv.supplierId === invoice?.supplierId &&
        (!inv.linkedFinalInvoiceId ||
          inv.linkedFinalInvoiceId === invoice?.id ||
          (invoice?.linkedProformaInvoiceIds || []).includes(inv.id)),
    )
    .map((inv) => ({ ...inv, poNumber: inv.purchaseOrderNumber }));

  // Kloppen beide kanten van de koppeling (lijst op de definitieve factuur, terugverwijzing op
  // de pro forma)? Zo niet, dan telt een aanbetaling op de ene plek wel en op de andere niet mee.
  const proformaLinkIssues = useMemo(
    () => findProformaLinkIssues(allInvoices).get(invoice?.id) || [],
    [allInvoices, invoice?.id],
  );

  // Use invoice's own currency
  const invoiceCurrency = invoice?.currency || "USD";

  // Determine if invoice is out of sync with Exact (compare last history subcollection entry with exactSyncedAt)
  const exactOutOfSync = useMemo(() => {
    if (!invoice?.exactPurchaseInvoiceId || !invoice?.exactSyncedAt) return false;
    if (!invoiceHistory || invoiceHistory.length === 0) return false;

    const lastHistoryEntry = invoiceHistory[invoiceHistory.length - 1];
    return lastHistoryEntry.changedAt > invoice.exactSyncedAt;
  }, [invoice, invoiceHistory]);

  // Load legacy payments from purchase orders using hook data
  useEffect(() => {
    if (!shipment?.sourceOrders || !purchaseOrders?.length) return;

    const allLegacyPayments = [];
    const orderIds = shipment.sourceOrders.map((so) => so.purchaseOrderId).filter(Boolean);

    for (const orderId of orderIds) {
      const order = purchaseOrders.find((po) => po.id === orderId);
      if (order && order.payments && Array.isArray(order.payments)) {
        for (const payment of order.payments) {
          allLegacyPayments.push({
            ...payment,
            sourceOrderNumber: order.orderNumber,
            sourceOrderId: orderId,
            isLegacy: true,
          });
        }
      }
    }

    setLegacyPayments(allLegacyPayments);
  }, [shipment, purchaseOrders]);

  // Payments recorded against linked proforma invoices, shown read-only
  // alongside this final invoice's own payments (never duplicated as a
  // payment record on the final invoice itself).
  const linkedProformaPayments = (rawLinkedProformaPayments || []).map((p) => ({
    ...p,
    key: `proforma-${p.invoiceId}-${p.id}`,
    _sourceProformaNumber: linkedProformaInvoices.find((inv) => inv.id === p.invoiceId)?.invoiceNumber,
  }));

  // Combine legacy payments with invoice payments (legacy first), plus
  // linked proforma payments (final invoices only)
  const allPayments = [
    ...legacyPayments.map((p) => ({ ...p, id: `legacy-${p.sourceOrderId}-${p.paidAt}` })),
    ...(payments || []),
    ...(invoice?.type === "final" ? linkedProformaPayments : []),
  ];

  // De regels zoals ze nu in beeld staan; `_index` in de tabel wijst hierin.
  const activeInvoiceLines = editingInvoice ? editedInvoiceLines : invoice?.lines || [];

  // Het origineel komt uit de zending. Koppelen gaat op positie binnen het product en
  // niet op productId alleen, want een zending kan hetzelfde product uit twee
  // inkooporders bevatten — zie functions/purchaseInvoiceLines.js.
  const getOriginalUnitPrice = (lineIndex) =>
    getOriginalUnitPriceForLine(activeInvoiceLines, shipment?.items, lineIndex);

  const getOriginalQuantity = (lineIndex) =>
    getOriginalQuantityForLine(activeInvoiceLines, shipment?.items, lineIndex);

  // Helper function to add history entry for invoice changes — writes to subcollection
  const addHistoryEntry = async (field, oldValue, newValue, action, metadata = null) => {
    if (!invoice) return;

    await firestore
      .collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE)
      .doc(invoice.id)
      .collection("history")
      .add({
        changedBy: email,
        changedAt: dayjs().unix(),
        changes: [
          {
            field,
            oldValue: oldValue ?? null,
            newValue: newValue ?? null,
            action,
            metadata: metadata ?? null,
          },
        ],
      });
  };

  // Herberekening staat in functions/firestore/proformaLinks.js, zodat het koppelen en de
  // betaling-handlers hieronder dezelfde berekening gebruiken.
  const recomputeFinalInvoiceTotals = (finalInvoiceId) => recomputeFinalInvoiceTotalsFor(finalInvoiceId, email);

  // Navigate away if invoice not found
  useEffect(() => {
    if (!invoiceLoading && !invoice) {
      // Net zelf verwijderd: handleDeleteInvoice meldt dat en navigeert zelf.
      if (deletedRef.current) return;
      message.error(t("invoice.not_found"));
      navigate(urls.purchaseOrderInvoicesList);
    }
  }, [invoiceLoading, invoice]);

  // Verwijderen: dezelfde controles als in de transactie, hier alvast om te tonen waarom het niet kan.
  const linkedFinalShipment = linkedFinalInvoice
    ? allShipments.find((ship) => ship.id === linkedFinalInvoice.shipmentId) || null
    : null;
  const deletionBlockers = invoice
    ? getInvoiceDeletionBlockers({
        invoice,
        paymentCount: payments.length,
        shipment: shipment || null,
        linkedFinalInvoice,
        linkedFinalShipment,
      })
    : [];

  const showDeletionBlocked = (blockers) =>
    Modal.warning({
      title: "Deze factuur kan niet verwijderd worden",
      content: (
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {blockers.map((text, i) => (
            <li key={i}>{text}</li>
          ))}
        </ul>
      ),
    });

  const handleDeleteInvoice = () => {
    if (deletionBlockers.length > 0) {
      showDeletionBlocked(deletionBlockers);
      return;
    }
    const consequences = [];
    if (invoice.type === "proforma") {
      if (linkedFinalInvoice) {
        consequences.push(
          `Hij wordt uit definitieve factuur ${linkedFinalInvoice.invoiceNumber} gehaald; die wordt opnieuw doorgerekend.`,
        );
      }
    } else {
      if (linkedProformaInvoices.length > 0) {
        consequences.push(
          `${linkedProformaInvoices.map((p) => p.invoiceNumber).join(", ")} ${
            linkedProformaInvoices.length === 1 ? "gaat" : "gaan"
          } terug naar "niet verrekend". De betalingen daarop blijven staan.`,
        );
      }
      if (shipment) consequences.push(`Zending ${shipment.shipmentNumber} heeft daarna geen factuur meer.`);
    }
    consequences.push("De geschiedenis en de PDF blijven bewaard; een kopie van de factuur gaat naar het logboek.");

    Modal.confirm({
      title: `Factuur ${invoice.invoiceNumber || ""} verwijderen?`,
      content: (
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {consequences.map((text, i) => (
            <li key={i}>{text}</li>
          ))}
        </ul>
      ),
      okText: "Verwijderen",
      okButtonProps: { danger: true },
      cancelText: "Annuleren",
      onOk: async () => {
        try {
          setDeletingInvoice(true);
          deletedRef.current = true;
          await deleteInvoice({ invoiceId: invoice.id, email, uid: uid || null });
          message.success(`Factuur ${invoice.invoiceNumber || ""} verwijderd`);
          navigate(urls.purchaseOrderInvoicesList);
        } catch (error) {
          deletedRef.current = false;
          console.error("Error deleting invoice:", error);
          if (error instanceof InvoiceDeletionBlockedError) showDeletionBlocked(error.blockers);
          else message.error("Fout bij verwijderen factuur: " + error.message);
        } finally {
          setDeletingInvoice(false);
        }
      },
    });
  };

  const handleUpdateInvoiceStatus = async (newStatus) => {
    if (!invoice) return;
    try {
      setSaving(true);
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      await addHistoryEntry("status", invoice.status, newStatus, "status_change");
      message.success("Factuurstatus bijgewerkt");
    } catch (error) {
      console.error("Error updating invoice status:", error);
      message.error("Fout bij bijwerken factuurstatus");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInvoiceNumber = async () => {
    if (!invoiceNumberValue || !invoiceNumberValue.trim()) {
      message.error("Voer een geldig factuurnummer in");
      return;
    }
    try {
      setSavingInvoiceNumber(true);
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        invoiceNumber: invoiceNumberValue.trim(),
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      await addHistoryEntry(
        "invoiceNumber",
        invoice.invoiceNumber,
        invoiceNumberValue.trim(),
        "field_change",
        `Factuurnummer gewijzigd: ${invoice.invoiceNumber} → ${invoiceNumberValue.trim()}`,
      );
      message.success("Factuurnummer opgeslagen");
      setEditingInvoiceNumber(false);
    } catch (error) {
      console.error("Error saving invoice number:", error);
      message.error("Fout bij opslaan factuurnummer");
    } finally {
      setSavingInvoiceNumber(false);
    }
  };

  const handleSaveInvoiceDate = async () => {
    if (!invoiceDateValue) {
      message.error("Selecteer een geldige factuurdatum");
      return;
    }
    try {
      setSavingInvoiceDate(true);
      const newDateUnix = invoiceDateValue.unix();
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        invoiceDate: newDateUnix,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      await addHistoryEntry(
        "invoiceDate",
        invoice.invoiceDate,
        newDateUnix,
        "field_change",
        `Factuurdatum gewijzigd naar ${invoiceDateValue.format("DD-MM-YYYY")}`,
      );
      message.success("Factuurdatum opgeslagen");
      setEditingInvoiceDate(false);
    } catch (error) {
      console.error("Error saving invoice date:", error);
      message.error("Fout bij opslaan factuurdatum");
    } finally {
      setSavingInvoiceDate(false);
    }
  };

  const handleSaveTotalAmount = async () => {
    if (totalAmountValue === null || totalAmountValue === undefined || totalAmountValue < 0) {
      message.error("Voer een geldig totaalbedrag in");
      return;
    }
    try {
      setSavingTotalAmount(true);
      const rounded = Math.round(totalAmountValue * 100) / 100;
      const newAmountDue = Math.round((rounded - (invoice.totalPaid || 0)) * 100) / 100;
      const newStatus = newAmountDue <= 0 ? "paid" : (invoice.totalPaid || 0) > 0 ? "partially_paid" : invoice.status;
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalAmount: rounded,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      await addHistoryEntry(
        "totalAmount",
        invoice.totalAmount,
        rounded,
        "field_change",
        `Totaalbedrag gewijzigd: ${formatPriceByCurrency(invoice.totalAmount, invoiceCurrency)} → ${formatPriceByCurrency(rounded, invoiceCurrency)}`,
      );
      message.success("Totaalbedrag opgeslagen");
      setEditingTotalAmount(false);
    } catch (error) {
      console.error("Error saving total amount:", error);
      message.error("Fout bij opslaan totaalbedrag");
    } finally {
      setSavingTotalAmount(false);
    }
  };

  const handleSyncToExact = async () => {
    if (!shipment?.sourceOrders?.[0]?.purchaseOrderNumber) {
      message.error("Geen ordernummer gevonden voor Exact synchronisatie");
      return;
    }

    setSyncingToExact(true);
    try {
      const idToken = await getFirebaseIDToken();
      const orderNumber = shipment.sourceOrders[0].purchaseOrderNumber;
      const endpoint = `${process.env.REACT_APP_API_URL || ""}/api/exact/purchase-orders/${orderNumber}/invoice`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken,
        },
        body: JSON.stringify({
          invoiceId: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          shipmentId: invoice.shipmentId,
          totalAmount: invoice.totalAmount,
          pdfUrl: invoice.pdfUrl || null,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data?.exactPurchaseInvoiceId) {
        await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
          exactPurchaseInvoiceId: data.exactPurchaseInvoiceId,
          exactSyncedAt: dayjs().unix(),
        });
        message.success("Factuur succesvol gesynchroniseerd met Exact");
      } else {
        message.warning("Factuur verzonden naar Exact, maar geen ID ontvangen");
      }
    } catch (error) {
      console.error("Error syncing to Exact:", error);
      message.error(`Fout bij synchroniseren met Exact: ${error.message}`);
    } finally {
      setSyncingToExact(false);
    }
  };

  const getStoragePathFromUrl = (url) => {
    try {
      const baseUrl = "https://firebasestorage.googleapis.com/v0/b/";
      if (url.startsWith(baseUrl)) {
        const pathStart = url.indexOf("/o/") + 3;
        const pathEnd = url.indexOf("?", pathStart);
        const encodedPath = pathEnd > -1 ? url.slice(pathStart, pathEnd) : url.slice(pathStart);
        return decodeURIComponent(encodedPath);
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleUploadInvoicePdf = async (file) => {
    if (!invoice) return;
    try {
      setUploadingInvoicePdf(true);
      const storage = getStorage();
      const fileName = `invoices/${invoice.id}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        pdfUrl: downloadURL,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      await addHistoryEntry("pdfUrl", invoice.pdfUrl || null, downloadURL, "field_change", "Factuur PDF geüpload");
      message.success("Factuur PDF geüpload");
    } catch (error) {
      console.error("Error uploading invoice PDF:", error);
      message.error("Fout bij uploaden factuur PDF");
    } finally {
      setUploadingInvoicePdf(false);
    }
  };

  const handleDeleteInvoicePdf = async () => {
    if (!invoice?.pdfUrl) return;
    try {
      setUploadingInvoicePdf(true);
      const previousUrl = invoice.pdfUrl;
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        pdfUrl: null,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      const storage = getStorage();
      const path = getStoragePathFromUrl(previousUrl);
      if (path) {
        const fileRef = ref(storage, path);
        await deleteObject(fileRef);
      }
      await addHistoryEntry("pdfUrl", previousUrl, null, "field_change", "Factuur PDF verwijderd");
      message.success("Factuur PDF verwijderd");
    } catch (error) {
      console.error("Error deleting invoice PDF:", error);
      message.error("Fout bij verwijderen factuur PDF");
    } finally {
      setUploadingInvoicePdf(false);
    }
  };

  const handleStartEditingInvoice = () => {
    setEditedInvoiceLines(JSON.parse(JSON.stringify(invoice.lines || [])));
    setEditedAdditionalCosts(JSON.parse(JSON.stringify(invoice.additionalCosts || [])));
    setEditedDiscountLines(JSON.parse(JSON.stringify(invoice.discountLines || [])));
    setEditingInvoice(true);
  };

  const handleCancelEditingInvoice = () => {
    setEditingInvoice(false);
    setEditedInvoiceLines([]);
    setEditedAdditionalCosts([]);
    setEditedDiscountLines([]);
  };

  const handleStartEditingProformaLinks = () => {
    setEditedLinkedProformaIds([...(invoice.linkedProformaInvoiceIds || [])]);
    setEditingProformaLinks(true);
  };

  const handleCancelEditingProformaLinks = () => {
    setEditingProformaLinks(false);
    setEditedLinkedProformaIds([]);
  };

  const handleSaveProformaLinks = async () => {
    try {
      setSavingProformaLinks(true);
      // Ook zonder zichtbare wijziging opslaan: de transactie herstelt dan een halve koppeling
      // (pro forma in de lijst, maar zonder terugverwijzing). Is alles al in orde, dan schrijft
      // hij niets.
      const { changed, totalsRepaired } = await saveProformaLinks({
        finalInvoiceId: invoice.id,
        desiredIds: editedLinkedProformaIds,
        email,
        invoicesById,
      });
      message.success(
        changed
          ? "Koppeling proforma facturen opgeslagen"
          : totalsRepaired
            ? "Betaald en openstaand bijgewerkt"
            : "Geen wijzigingen",
      );
      setEditingProformaLinks(false);
      setEditedLinkedProformaIds([]);
    } catch (error) {
      console.error("Error saving proforma links:", error);
      if (error instanceof ProformaLinkConflictError) {
        Modal.error({
          title: "Koppeling niet opgeslagen",
          content: (
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {error.messages.map((text, i) => (
                <li key={i}>{text}</li>
              ))}
            </ul>
          ),
        });
      } else {
        message.error("Fout bij opslaan koppeling: " + error.message);
      }
    } finally {
      setSavingProformaLinks(false);
    }
  };

  const handleAddDiscountLine = () => {
    setEditedDiscountLines([
      ...editedDiscountLines,
      {
        id: `discount_${Date.now()}`,
        description: "",
        amount: 0,
        createdAt: dayjs().unix(),
        createdBy: email,
      },
    ]);
  };

  const handleRemoveDiscountLine = (discountId) => {
    setEditedDiscountLines(editedDiscountLines.filter((d) => d.id !== discountId));
  };

  const handleDiscountLineChange = (discountId, field, value) => {
    setEditedDiscountLines(
      editedDiscountLines.map((d) => {
        if (d.id !== discountId) return d;
        if (field === "amount") {
          const num = value === null || value === undefined ? 0 : -Math.abs(value);
          return { ...d, amount: num };
        }
        return { ...d, [field]: value };
      }),
    );
  };

  const handleAddInvoiceLine = () => {
    if (!selectedProduct) {
      message.error("Selecteer een product");
      return;
    }

    const newLine = {
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      productColor: selectedProduct.color,
      productCode: selectedProduct.code,
      quantity: newLineQuantity,
      unitPrice: newLineUnitPrice,
      discountPercentage: newLineDiscount,
      totalPrice: newLineQuantity * newLineUnitPrice * (1 - newLineDiscount / 100),
    };

    setEditedInvoiceLines([...editedInvoiceLines, newLine]);
    setShowAddLineModal(false);
    setSelectedProduct(null);
    setNewLineQuantity(1);
    setNewLineUnitPrice(0);
    setNewLineDiscount(0);
    message.success("Factuurregel toegevoegd");
  };

  const handleRemoveInvoiceLine = (index) => {
    const updatedLines = editedInvoiceLines.filter((_, i) => i !== index);
    setEditedInvoiceLines(updatedLines);
    message.success("Factuurregel verwijderd");
  };

  const handleOpenAddLineModal = () => {
    setSelectedProduct(null);
    setNewLineQuantity(1);
    setNewLineUnitPrice(0);
    setNewLineDiscount(0);
    setShowAddLineModal(true);
  };

  const handleCloseAddLineModal = () => {
    setShowAddLineModal(false);
    setSelectedProduct(null);
    setNewLineQuantity(1);
    setNewLineUnitPrice(0);
    setNewLineDiscount(0);
  };

  const handleProductSelect = (productId) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setNewLineUnitPrice(product.purchasePrice || 0);
    }
  };

  const handleInvoiceLineChange = (index, field, value) => {
    const updated = [...editedInvoiceLines];
    const line = { ...updated[index] };

    if (field === "quantity" || field === "unitPrice" || field === "discountPercentage") {
      line[field] = value;
      // Recalculate totalPrice for this line
      const qty = field === "quantity" ? value : line.quantity || 0;
      const price = field === "unitPrice" ? value : line.unitPrice || 0;
      const discount = field === "discountPercentage" ? value : line.discountPercentage || 0;
      line.totalPrice = qty * price * (1 - discount / 100);
    } else {
      line[field] = value;
    }

    updated[index] = line;
    setEditedInvoiceLines(updated);
  };

  const handleAdditionalCostChange = (index, field, value) => {
    const updated = [...editedAdditionalCosts];
    updated[index] = { ...updated[index], [field]: value };
    setEditedAdditionalCosts(updated);
  };

  const handleSaveInvoiceEdits = async () => {
    try {
      setSaving(true);

      // Validate discount lines
      for (const d of editedDiscountLines) {
        if (!d.description || !d.description.trim()) {
          message.error("Geef een omschrijving voor elke kortingsregel");
          setSaving(false);
          return;
        }
        if (!d.amount || d.amount >= 0) {
          message.error("Kortingsbedragen moeten negatief zijn");
          setSaving(false);
          return;
        }
      }

      // Normalize discount amounts to be strictly negative
      const normalizedDiscountLines = editedDiscountLines.map((d) => ({
        ...d,
        amount: -Math.abs(d.amount || 0),
      }));

      // Recalculate totals
      const subtotalProducts = editedInvoiceLines.reduce((sum, line) => sum + (line.totalPrice || 0), 0);
      const subtotalAdditionalCosts = editedAdditionalCosts.reduce((sum, cost) => sum + (cost.amount || 0), 0);
      const subtotalDiscounts = normalizedDiscountLines.reduce((sum, d) => sum + (d.amount || 0), 0);
      const totalAmount = subtotalProducts + subtotalAdditionalCosts + subtotalDiscounts;
      const currentTotalPaid = invoice.totalPaid || 0;
      const amountDue = totalAmount - currentTotalPaid;

      // Track specific line changes
      const lineChanges = [];
      const oldLines = invoice.lines || [];
      const newLines = editedInvoiceLines;

      // Create maps for easier comparison (use productId instead of productCode)
      const oldLinesMap = new Map(oldLines.map((line, idx) => [line.productId, { ...line, originalIndex: idx }]));
      const newLinesMap = new Map(newLines.map((line, idx) => [line.productId, { ...line, originalIndex: idx }]));

      // Find added lines
      newLines.forEach((newLine, idx) => {
        if (!oldLinesMap.has(newLine.productId)) {
          lineChanges.push({
            field: "line_added",
            metadata: `+ Regel ${idx + 1}: ${newLine.productName} (${newLine.productId}) - ${newLine.quantity}x ${formatPriceByCurrency(newLine.unitPrice, invoiceCurrency)}`,
          });
        } else {
          const oldLine = oldLinesMap.get(newLine.productId);
          // Check for changes in quantity
          if (oldLine.quantity !== newLine.quantity) {
            lineChanges.push({
              field: "line_quantity",
              metadata: `~ ${newLine.productName}: aantal ${oldLine.quantity} → ${newLine.quantity}`,
            });
          }
          // Check for changes in unit price
          if (oldLine.unitPrice !== newLine.unitPrice) {
            lineChanges.push({
              field: "line_price",
              metadata: `~ ${newLine.productName}: prijs ${formatPriceByCurrency(oldLine.unitPrice, invoiceCurrency)} → ${formatPriceByCurrency(newLine.unitPrice, invoiceCurrency)}`,
            });
          }
          // Check for changes in discount
          if (oldLine.discountPercentage !== newLine.discountPercentage) {
            lineChanges.push({
              field: "line_discount",
              metadata: `~ ${newLine.productName}: korting ${oldLine.discountPercentage}% → ${newLine.discountPercentage}%`,
            });
          }
        }
      });

      // Find removed lines
      oldLines.forEach((oldLine) => {
        if (!newLinesMap.has(oldLine.productId)) {
          lineChanges.push({ field: "line_removed", metadata: `- ${oldLine.productName} verwijderd` });
        }
      });

      // Track discount line changes
      const oldDiscountLines = invoice.discountLines || [];
      const oldDiscountMap = new Map(oldDiscountLines.map((d) => [d.id, d]));
      const newDiscountMap = new Map(normalizedDiscountLines.map((d) => [d.id, d]));
      normalizedDiscountLines.forEach((d) => {
        const old = oldDiscountMap.get(d.id);
        if (!old) {
          lineChanges.push({
            field: "discount_added",
            metadata: `+ Korting: ${d.description} (${formatPriceByCurrency(d.amount, invoiceCurrency)})`,
          });
        } else if (old.description !== d.description || old.amount !== d.amount) {
          lineChanges.push({
            field: "discount_changed",
            metadata: `~ Korting: ${old.description} ${formatPriceByCurrency(old.amount, invoiceCurrency)} → ${d.description} ${formatPriceByCurrency(d.amount, invoiceCurrency)}`,
          });
        }
      });
      oldDiscountLines.forEach((d) => {
        if (!newDiscountMap.has(d.id)) {
          lineChanges.push({ field: "discount_removed", metadata: `- Korting verwijderd: ${d.description}` });
        }
      });

      // Update invoice
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        lines: editedInvoiceLines,
        additionalCosts: editedAdditionalCosts,
        discountLines: normalizedDiscountLines,
        subtotalProducts,
        subtotalAdditionalCosts,
        subtotalDiscounts,
        totalAmount,
        amountDue,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      // Record all line/cost/discount changes from this save as one grouped
      // history entry, so the exact moment of a change stays traceable.
      // (Proforma link/unlink changes are saved separately from the
      // dedicated "Gekoppelde proforma's" card, with their own history entry.)
      if (lineChanges.length > 0) {
        await firestore
          .collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE)
          .doc(invoice.id)
          .collection("history")
          .add({
            changedBy: email,
            changedAt: dayjs().unix(),
            changes: lineChanges.map((c) => ({
              field: c.field,
              oldValue: null,
              newValue: null,
              action: "invoice_edit",
              metadata: c.metadata,
            })),
          });
      }

      message.success("Factuurwijzigingen opgeslagen");
      setEditingInvoice(false);
      setEditedInvoiceLines([]);
      setEditedAdditionalCosts([]);
      setEditedDiscountLines([]);
    } catch (error) {
      console.error("Error saving invoice edits:", error);
      message.error("Fout bij opslaan factuurwijzigingen");
    } finally {
      setSaving(false);
    }
  };

  const resetPaymentForm = () => {
    setPaymentAmount(null);
    setPaymentDate(dayjs());
    setPaymentReference("");
    setPaymentNotes("");
    setPaymentProofFile(null);
    setPaymentExchangeRate(null);
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentAmount(payment.amount);
    setPaymentDate(payment.paymentDate ? dayjs.unix(payment.paymentDate) : dayjs());
    setPaymentReference(payment.reference || "");
    setPaymentNotes(payment.notes || "");
    setPaymentProofFile(null);
    setPaymentExchangeRate(payment.exchangeRate || null);
    setShowEditPaymentModal(true);
  };

  const handleUploadPaymentProof = async (file) => {
    setUploadingPaymentProof(true);
    try {
      const storage = getStorage();
      const fileName = `payment-proofs/${invoice.id}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setPaymentProofFile(downloadURL);
      message.success("Bewijs geüpload");
    } catch (error) {
      console.error("Error uploading proof:", error);
      message.error("Fout bij uploaden bewijs");
    } finally {
      setUploadingPaymentProof(false);
    }
  };

  const handleAddPayment = async () => {
    if (!paymentAmount || paymentAmount <= 0) {
      message.error("Voer een geldig bedrag in");
      return;
    }

    try {
      setSavingPayment(true);

      const paymentData = {
        amount: paymentAmount,
        paymentDate: paymentDate.unix(),
        reference: paymentReference,
        notes: paymentNotes,
        proofUrl: paymentProofFile,
        exchangeRate: paymentExchangeRate,
        createdAt: dayjs().unix(),
        createdBy: email,
      };

      // Add payment to invoice
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).add({
        ...paymentData,
        invoiceId: invoice.id,
      });

      // Update invoice totals (rounded to 2 decimals to avoid floating-point drift)
      const newTotalPaid = Math.round(((invoice.totalPaid || 0) + paymentAmount) * 100) / 100;
      const newAmountDue = Math.round((invoice.totalAmount - newTotalPaid) * 100) / 100;
      const newStatus = newAmountDue <= 0 ? "paid" : newTotalPaid > 0 ? "partially_paid" : invoice.status;

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      // This payment was made on a proforma linked to a final invoice — that
      // final invoice's cached totals are now stale, refresh them.
      if (invoice.type === "proforma" && invoice.linkedFinalInvoiceId) {
        await recomputeFinalInvoiceTotals(invoice.linkedFinalInvoiceId);
      }

      message.success("Betaling toegevoegd");
      setShowPaymentModal(false);
      resetPaymentForm();
    } catch (error) {
      console.error("Error adding payment:", error);
      message.error("Fout bij toevoegen betaling");
    } finally {
      setSavingPayment(false);
    }
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment) return;

    try {
      setSavingPayment(true);

      const updates = {
        amount: paymentAmount,
        paymentDate: paymentDate.unix(),
        reference: paymentReference,
        notes: paymentNotes,
        exchangeRate: paymentExchangeRate,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      };

      if (paymentProofFile) {
        updates.proofUrl = paymentProofFile;
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).doc(editingPayment.id).update(updates);

      // Recalculate invoice totals (rounded to 2 decimals to avoid floating-point drift)
      const otherPayments = payments.filter((p) => p.id !== editingPayment.id);
      const otherPaymentsTotal = otherPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const newTotalPaid = Math.round((otherPaymentsTotal + paymentAmount) * 100) / 100;
      const newAmountDue = Math.round((invoice.totalAmount - newTotalPaid) * 100) / 100;
      const newStatus = newAmountDue <= 0 ? "paid" : newTotalPaid > 0 ? "partially_paid" : invoice.status;

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      if (invoice.type === "proforma" && invoice.linkedFinalInvoiceId) {
        await recomputeFinalInvoiceTotals(invoice.linkedFinalInvoiceId);
      }

      const paymentChanges = [];
      if (editingPayment.amount !== paymentAmount) {
        paymentChanges.push(
          `bedrag ${formatPriceByCurrency(editingPayment.amount, invoiceCurrency)} → ${formatPriceByCurrency(paymentAmount, invoiceCurrency)}`,
        );
      }
      if ((editingPayment.reference || "") !== (paymentReference || "")) {
        paymentChanges.push(`referentie "${editingPayment.reference || "—"}" → "${paymentReference || "—"}"`);
      }
      const oldDate = editingPayment.paymentDate ? dayjs.unix(editingPayment.paymentDate).format("DD-MM-YYYY") : null;
      const newDate = paymentDate ? dayjs(paymentDate).format("DD-MM-YYYY") : null;
      if (oldDate !== newDate) {
        paymentChanges.push(`datum ${oldDate || "—"} → ${newDate || "—"}`);
      }
      if ((editingPayment.notes || "") !== (paymentNotes || "")) {
        paymentChanges.push("notitie gewijzigd");
      }
      if ((editingPayment.exchangeRate ?? null) !== (paymentExchangeRate ?? null)) {
        paymentChanges.push(`wisselkoers ${editingPayment.exchangeRate ?? "—"} → ${paymentExchangeRate ?? "—"}`);
      }
      if (paymentChanges.length > 0) {
        await addHistoryEntry(
          "payment_updated",
          editingPayment.amount,
          paymentAmount,
          "payment_updated",
          `Betaling bijgewerkt: ${paymentChanges.join(", ")}`,
        );
      }

      message.success("Betaling bijgewerkt");
      setShowEditPaymentModal(false);
      setEditingPayment(null);
      resetPaymentForm();
    } catch (error) {
      console.error("Error updating payment:", error);
      message.error("Fout bij bijwerken betaling");
    } finally {
      setSavingPayment(false);
    }
  };

  const resetCorrectionForm = () => {
    setCorrectionAmount(null);
    setCorrectionIsNegative(false);
    setCorrectionDate(dayjs());
    setCorrectionReason("");
    setCorrectionReference("");
  };

  const handleDeletePayment = async (record) => {
    try {
      setDeletingPaymentId(record.id);

      if (record.isLegacy) {
        // Remove from purchaseOrders.payments array
        const orderRef = firestore.collection("purchaseOrders").doc(record.sourceOrderId);
        const orderDoc = await orderRef.get();
        if (orderDoc.exists) {
          const currentPayments = orderDoc.data().payments || [];
          const updatedPayments = currentPayments.filter((p) => {
            // Match by paidAt timestamp and amount
            return !(p.paidAt === record.paidAt && p.amount === record.amount);
          });
          await orderRef.update({ payments: updatedPayments });
        }
      } else {
        await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).doc(record.id).delete();
      }

      // Recalculate totals from remaining non-legacy payments
      const remainingPayments = payments.filter((p) => p.id !== record.id);
      const newTotalPaid = Math.round(remainingPayments.reduce((sum, p) => sum + (p.amount || 0), 0) * 100) / 100;
      const newAmountDue = Math.round((invoice.totalAmount - newTotalPaid) * 100) / 100;
      const newStatus = newAmountDue <= 0 ? "paid" : newTotalPaid > 0 ? "partially_paid" : "submitted";

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      if (invoice.type === "proforma" && invoice.linkedFinalInvoiceId) {
        await recomputeFinalInvoiceTotals(invoice.linkedFinalInvoiceId);
      }

      await addHistoryEntry(
        "payment_deleted",
        record.amount,
        null,
        "payment_deleted",
        `Betaling verwijderd: ${formatPriceByCurrency(record.amount, invoiceCurrency)}${record.isLegacy ? " (legacy)" : ""}`,
      );

      message.success("Betaling verwijderd");
    } catch (error) {
      console.error("Error deleting payment:", error);
      message.error("Fout bij verwijderen betaling");
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const handleRecalculateTotals = async () => {
    try {
      setRecalculating(true);
      const newTotalPaid = Math.round(payments.reduce((sum, p) => sum + (p.amount || 0), 0) * 100) / 100;
      const newAmountDue = Math.round((invoice.totalAmount - newTotalPaid) * 100) / 100;
      const newStatus = newAmountDue <= 0 ? "paid" : newTotalPaid > 0 ? "partially_paid" : "submitted";

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      if (invoice.type === "proforma" && invoice.linkedFinalInvoiceId) {
        await recomputeFinalInvoiceTotals(invoice.linkedFinalInvoiceId);
      }

      await addHistoryEntry(
        "totals_recalculated",
        invoice.totalPaid,
        newTotalPaid,
        "totals_recalculated",
        `Totalen herberekend: betaald ${formatPriceByCurrency(invoice.totalPaid, invoiceCurrency)} → ${formatPriceByCurrency(newTotalPaid, invoiceCurrency)}`,
      );

      message.success("Totalen herberekend");
    } catch (error) {
      console.error("Error recalculating totals:", error);
      message.error("Fout bij herberekenen totalen");
    } finally {
      setRecalculating(false);
    }
  };

  const handleAddCorrection = async () => {
    if (!correctionAmount || correctionAmount <= 0) {
      message.error("Voer een geldig bedrag in");
      return;
    }
    if (!correctionReason || !correctionReason.trim()) {
      message.error("Voer een reden in voor de correctie");
      return;
    }

    try {
      setSavingCorrection(true);

      const signedAmount = correctionIsNegative ? -Math.abs(correctionAmount) : Math.abs(correctionAmount);

      const correctionData = {
        invoiceId: invoice.id,
        type: "correction",
        amount: signedAmount,
        paymentDate: correctionDate.unix(),
        reference: correctionReference || null,
        notes: correctionReason,
        createdAt: dayjs().unix(),
        createdBy: email,
      };

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).add(correctionData);

      const newTotalPaid = Math.round(((invoice.totalPaid || 0) + signedAmount) * 100) / 100;
      const newAmountDue = Math.round((invoice.totalAmount - newTotalPaid) * 100) / 100;
      const newStatus = newAmountDue <= 0 ? "paid" : newTotalPaid > 0 ? "partially_paid" : invoice.status;

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      if (invoice.type === "proforma" && invoice.linkedFinalInvoiceId) {
        await recomputeFinalInvoiceTotals(invoice.linkedFinalInvoiceId);
      }

      await addHistoryEntry(
        "correction_added",
        invoice.totalPaid || 0,
        newTotalPaid,
        "correction_added",
        `Correctie: ${signedAmount > 0 ? "+" : ""}${formatPriceByCurrency(signedAmount, invoiceCurrency)} — ${correctionReason}`,
      );

      message.success("Correctie toegevoegd");
      setShowCorrectionModal(false);
      resetCorrectionForm();
    } catch (error) {
      console.error("Error adding correction:", error);
      message.error("Fout bij toevoegen correctie");
    } finally {
      setSavingCorrection(false);
    }
  };

  if (invoiceLoading) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!invoice) {
    return <div style={{ padding: 24 }}>Factuur niet gevonden</div>;
  }

  // True when the sum of all purchaseOrderPayments doesn't match the stored totalPaid (indicates drift)
  // For final invoices, include payments from linked proforma invoices
  const relevantPayments = [
    ...(payments || []),
    ...(invoice?.type === "final" ? linkedProformaPayments : []),
  ];
  const paymentsSum = Math.round(relevantPayments.reduce((sum, p) => sum + (p.amount || 0), 0) * 100) / 100;
  const storedTotalPaid = Math.round((invoice.totalPaid || 0) * 100) / 100;
  const totalsMismatch = Math.abs(paymentsSum - storedTotalPaid) >= 0.01;

  // Detailvelden van een betaling, achter de uitklap van de betalingentabel.
  const renderPaymentDetails = (payment) => (
    <KeyValueList
      labelWidth={120}
      items={[
        {
          label: "Bron",
          value: payment._sourceProformaNumber ? (
            <RecordLink to={`${urls.purchaseOrderInvoiceDetail}${payment.invoiceId}`}>
              Pro forma {payment._sourceProformaNumber}
            </RecordLink>
          ) : payment.isLegacy ? (
            `Aanbetaling via ${payment.sourceOrderNumber || "inkooporder"}`
          ) : (
            "Deze factuur"
          ),
        },
        payment.migratedFromLegacy ? { label: "Herkomst", value: <Tag color="purple">Gemigreerd</Tag> } : null,
        {
          label: "Notities",
          value: payment.notes || (payment.isLegacy ? "Aanbetaling via inkooporder" : null),
          wrap: true,
        },
        {
          label: "Bewijs",
          value: payment.proofUrl ? (
            <a href={payment.proofUrl} target="_blank" rel="noopener noreferrer">
              Bekijken
            </a>
          ) : null,
        },
        { label: "Door", value: payment.createdBy || payment.paidBy },
      ]}
    />
  );

  // Gewogen gemiddelde koers: som(bedrag × koers) / som(bedrag) over de
  // betalingen die een eigen koers hebben. Zelfde weging als de ontvangst-flow
  // op Zending detail gebruikt.
  const rateWeighting = allPayments.reduce(
    (acc, payment) => {
      if (!payment.exchangeRate || !payment.amount) return acc;
      return { amount: acc.amount + payment.amount, weighted: acc.weighted + payment.amount * payment.exchangeRate };
    },
    { amount: 0, weighted: 0 },
  );
  const weightedExchangeRate = rateWeighting.amount > 0 ? rateWeighting.weighted / rateWeighting.amount : null;
  const paymentsWithoutRate = allPayments.filter((payment) => !payment.exchangeRate).length;

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title={invoice.invoiceNumber || "Factuur"}
        meta={
          <>
            <span
              style={{
                fontSize: 10.5,
                letterSpacing: "0.09em",
                textTransform: "uppercase",
                color: invoice.type === "proforma" ? "#96601A" : "rgba(0, 0, 0, 0.4)",
              }}
            >
              {invoice.type === "proforma" ? "Pro forma" : "Definitief"}
            </span>
            <span style={{ color: "rgba(0, 0, 0, 0.15)" }}>·</span>
            <span>{invoice.supplierName || "-"}</span>
            {invoice.invoiceDate && (
              <>
                <span style={{ color: "rgba(0, 0, 0, 0.15)" }}>·</span>
                <span>
                  factuurdatum <Num>{dayjs.unix(invoice.invoiceDate).format("DD-MM-YYYY")}</Num>
                </span>
              </>
            )}
            <span style={{ color: "rgba(0, 0, 0, 0.15)" }}>·</span>
            <Num>{invoiceCurrency}</Num>
          </>
        }
        extra={
          <>
            <Select
              value={invoice.status || "submitted"}
              style={{ minWidth: 160 }}
              onChange={handleUpdateInvoiceStatus}
              loading={saving}
              options={Object.entries(invoiceStatusLabels).map(([value, label]) => ({ value, label }))}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                resetPaymentForm();
                setPaymentAmount(invoice.amountDue > 0 ? invoice.amountDue : null);
                setShowPaymentModal(true);
              }}
            >
              Betaling toevoegen
            </Button>
            {/* Verwijderen is zeldzaam en onomkeerbaar: onder een menu, niet als knop naast de
                primaire actie (zelfde patroon als de uitzonderingsacties op de zendingpagina). */}
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [{ key: "delete", label: "Factuur verwijderen", icon: <DeleteOutlined />, danger: true }],
                onClick: ({ key }) => {
                  if (key === "delete") handleDeleteInvoice();
                },
              }}
            >
              <Button icon={<MoreOutlined />} loading={deletingInvoice} aria-label="Meer acties" />
            </Dropdown>
          </>
        }
      />

      <DetailLayout
        aside={
          <>
            {/* Betaalstand vooraan: dit is waar iemand een factuur voor opent.
                Stond eerder als "Betaald / Openstaand" ergens halverwege een
                raster van twintig velden. */}
            <SectionCard title="Betaling">
              <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.55)" }}>Openstaand</span>
                  <Num
                    size={21}
                    strong
                    style={{ color: (invoice.amountDue ?? invoice.totalAmount ?? 0) > 0 ? "#9E2F2F" : "#2F6B33" }}
                  >
                    {formatPriceByCurrency(invoice.amountDue ?? invoice.totalAmount ?? 0, invoiceCurrency)}
                  </Num>
                </div>
                <div style={{ height: 1, background: "rgba(5, 5, 5, 0.06)" }} />
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.55)" }}>Betaald</span>
                  <Num>{formatPriceByCurrency(invoice.totalPaid || 0, invoiceCurrency)}</Num>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.55)" }}>Totaalbedrag</span>
                  <Num strong size={14}>
                    {formatPriceByCurrency(invoice.totalAmount || 0, invoiceCurrency)}
                  </Num>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Gegevens">
              <KeyValueList
                labelWidth={116}
                items={[
                  {
                    label: "Status",
                    value: (
                      <Tag color={invoiceStatusColors[invoice.status] || "default"}>
                        {invoiceStatusLabels[invoice.status] || invoice.status || "Ingediend"}
                      </Tag>
                    ),
                  },
                  { label: "Soort", value: invoice.type === "proforma" ? "Pro forma" : "Definitief" },
                  { label: "Leverancier", value: invoice.supplierName },
                  {
                    label: "Factuurnummer",
                    value: editingInvoiceNumber ? (
                      <Space.Compact style={{ width: "100%" }}>
                        <Input
                          size="small"
                          value={invoiceNumberValue}
                          onChange={(e) => setInvoiceNumberValue(e.target.value)}
                          onPressEnter={handleSaveInvoiceNumber}
                          autoFocus
                        />
                        <Button
                          size="small"
                          type="primary"
                          icon={<CheckOutlined />}
                          title="Opslaan"
                          loading={savingInvoiceNumber}
                          onClick={handleSaveInvoiceNumber}
                        />
                        <Button
                          size="small"
                          icon={<CloseOutlined />}
                          title="Annuleren"
                          onClick={() => setEditingInvoiceNumber(false)}
                        />
                      </Space.Compact>
                    ) : (
                      invoice.invoiceNumber && <Num>{invoice.invoiceNumber}</Num>
                    ),
                    action: editingInvoiceNumber ? null : (
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        title="Bewerken"
                        onClick={() => {
                          setInvoiceNumberValue(invoice.invoiceNumber || "");
                          setEditingInvoiceNumber(true);
                        }}
                      />
                    ),
                  },
                  {
                    label: "Factuurdatum",
                    value: editingInvoiceDate ? (
                      <Space size={4}>
                        <DatePicker
                          size="small"
                          format="DD-MM-YYYY"
                          value={invoiceDateValue}
                          onChange={(date) => setInvoiceDateValue(date)}
                        />
                        <Button
                          size="small"
                          type="primary"
                          icon={<CheckOutlined />}
                          title="Opslaan"
                          loading={savingInvoiceDate}
                          onClick={handleSaveInvoiceDate}
                        />
                        <Button
                          size="small"
                          icon={<CloseOutlined />}
                          title="Annuleren"
                          onClick={() => setEditingInvoiceDate(false)}
                        />
                      </Space>
                    ) : (
                      invoice.invoiceDate && <Num>{dayjs.unix(invoice.invoiceDate).format("DD-MM-YYYY")}</Num>
                    ),
                    action: editingInvoiceDate ? null : (
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        title="Bewerken"
                        onClick={() => {
                          setInvoiceDateValue(invoice.invoiceDate ? dayjs.unix(invoice.invoiceDate) : dayjs());
                          setEditingInvoiceDate(true);
                        }}
                      />
                    ),
                  },
                  invoice.createdAt
                    ? {
                        label: "Aangemaakt",
                        value: <Num>{dayjs.unix(invoice.createdAt).format("DD-MM-YYYY HH:mm")}</Num>,
                      }
                    : null,
                  {
                    label: "Subtotaal producten",
                    value: <Num>{formatPriceByCurrency(invoice.subtotalProducts || 0, invoiceCurrency)}</Num>,
                  },
                  {
                    label: "Bijkomende kosten",
                    value: <Num>{formatPriceByCurrency(invoice.subtotalAdditionalCosts || 0, invoiceCurrency)}</Num>,
                  },
                  invoice.discountLines?.length > 0 || (invoice.subtotalDiscounts || 0) !== 0
                    ? {
                        label: "Kortingen",
                        value: (
                          <Num style={{ color: "#9E2F2F" }}>
                            {formatPriceByCurrency(invoice.subtotalDiscounts || 0, invoiceCurrency)}
                          </Num>
                        ),
                      }
                    : null,
                  {
                    label: "Totaalbedrag",
                    // Alleen bij een pro forma is het totaal handmatig: een
                    // definitieve factuur telt zijn regels op.
                    value:
                      invoice.type === "proforma" && editingTotalAmount ? (
                        <Space.Compact style={{ width: "100%" }}>
                          <InputNumber
                            size="small"
                            min={0}
                            step={0.01}
                            value={totalAmountValue}
                            onChange={(v) => setTotalAmountValue(v)}
                            style={{ width: "100%" }}
                            autoFocus
                          />
                          <Button
                          size="small"
                          type="primary"
                          icon={<CheckOutlined />}
                          title="Opslaan"
                          loading={savingTotalAmount}
                          onClick={handleSaveTotalAmount}
                        />
                          <Button
                            size="small"
                            icon={<CloseOutlined />}
                            title="Annuleren"
                            onClick={() => setEditingTotalAmount(false)}
                          />
                        </Space.Compact>
                      ) : (
                        <Num strong size={14}>
                          {formatPriceByCurrency(invoice.totalAmount || 0, invoiceCurrency)}
                        </Num>
                      ),
                    // Alleen een pro forma heeft een handmatig totaal; een
                    // definitieve factuur telt zijn regels op.
                    action:
                      invoice.type === "proforma" && !editingTotalAmount ? (
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          title="Bewerken"
                          onClick={() => {
                            setTotalAmountValue(invoice.totalAmount || 0);
                            setEditingTotalAmount(true);
                          }}
                        />
                      ) : null,
                  },
                ]}
              />
            </SectionCard>

            {/* Waar deze factuur aan hangt. Stond als losse blauwe tags tussen de
                gegevens; als eigen blok is de route naar order/zending duidelijk. */}
            <SectionCard title="Koppelingen">
              {!shipment && !order && !linkedFinalInvoice ? (
                <div style={{ padding: "14px 20px", fontSize: 13, color: "rgba(0, 0, 0, 0.4)" }}>
                  Nog niets gekoppeld.
                </div>
              ) : (
                <div>
                  {shipment && (
                    <Link
                      to={`${urls.purchaseOrderShipmentDetail}${shipment.id}`}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        gap: 10,
                        alignItems: "center",
                        padding: "10px 20px",
                        borderBottom: "1px solid rgba(5, 5, 5, 0.04)",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)" }}>Zending</span>
                        <Num>{shipment.shipmentNumber}</Num>
                      </div>
                    </Link>
                  )}
                  {order && (
                    <Link
                      to={`${urls.purchaseOrderDetail}${order.id}`}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        gap: 10,
                        alignItems: "center",
                        padding: "10px 20px",
                        borderBottom: "1px solid rgba(5, 5, 5, 0.04)",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)" }}>Inkooporder</span>
                        <Num>{order.orderNumber}</Num>
                      </div>
                    </Link>
                  )}
                  {invoice.type === "proforma" && (
                    <div style={{ padding: "10px 20px" }}>
                      <div style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)", marginBottom: 2 }}>
                        Verrekend met
                      </div>
                      {linkedFinalInvoice ? (
                        <Space size={8} wrap>
                          <RecordLink to={`${urls.purchaseOrderInvoiceDetail}${linkedFinalInvoice.id}`}>
                            {linkedFinalInvoice.invoiceNumber}
                          </RecordLink>
                          {invoice.linkedAt && (
                            <span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)" }}>
                              op {dayjs.unix(invoice.linkedAt).format("DD-MM-YYYY")}
                            </span>
                          )}
                        </Space>
                      ) : (
                        <span style={{ fontSize: 13, color: "#96601A" }}>Nog niet verrekend</span>
                      )}
                      {proformaLinkIssues.map((text, i) => (
                        <div key={i} style={{ fontSize: 12, color: "#cf1322", marginTop: 4 }}>
                          <WarningOutlined /> {text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </SectionCard>
          </>
        }
      >
        <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <h5 style={{ margin: 0 }}>Factuurregels</h5>
          <Space>
            {!editingInvoice && invoice.type === "final" && (
              <ExcelExport
                data={getPurchaseOrderInvoiceExcelExportData(invoice, invoiceCurrency, products)}
                fileName={`Factuur ${invoice.invoiceNumber || id}`}
              >
                <Button size="small" icon={<FileExcelOutlined />}>
                  Exporteren
                </Button>
              </ExcelExport>
            )}
            {editingInvoice && (
              <Button size="small" icon={<PlusOutlined />} onClick={handleOpenAddLineModal}>
                Regel toevoegen
              </Button>
            )}
            {editingInvoice && (
              <Button size="small" icon={<PlusOutlined />} onClick={handleAddDiscountLine}>
                Kortingsregel toevoegen
              </Button>
            )}
            {!editingInvoice && (
              <Button size="small" onClick={handleStartEditingInvoice}>
                Factuur aanpassen
              </Button>
            )}
            {editingInvoice && (
              <Space>
                <Button size="small" onClick={handleCancelEditingInvoice}>
                  Annuleren
                </Button>
                <Button size="small" type="primary" onClick={handleSaveInvoiceEdits} loading={saving}>
                  Opslaan
                </Button>
              </Space>
            )}
          </Space>
        </div>

        {/* Een aanbetaling heeft geen regels, alleen een totaalbedrag. Zonder deze
            uitleg leek de kaart gewoon leeg/stuk. */}
        {(editingInvoice ? editedInvoiceLines : invoice.lines || []).length === 0 && (
          <div
            style={{
              padding: "32px 16px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 500 }}>
              {invoice.type === "proforma" ? "Geen regels op deze pro forma" : "Nog geen factuurregels"}
            </span>
            <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.45)", maxWidth: 420, lineHeight: 1.5 }}>
              {invoice.type === "proforma"
                ? "Een aanbetaling heeft alleen een totaalbedrag. De regels staan op de definitieve factuur."
                : "Voeg regels toe of upload de factuur-PDF."}
            </span>
          </div>
        )}

        {(editingInvoice ? editedInvoiceLines : invoice.lines || []).length > 0 && (
          <Table
            // Zonder scroll.x loopt de tabel bij een smalle hoofdkolom buiten zijn kaart.
            scroll={{ x: 860 }}
            // Dezelfde lijst als waar `getOriginalQuantity`/`getOriginalUnitPrice` in
            // zoeken: `_index` moet in beide hetzelfde betekenen.
            dataSource={activeInvoiceLines.map((l, i) => ({
              ...l,
              key: i,
              _index: i,
            }))}
            pagination={false}
            size="small"
            style={{ marginTop: 12, marginBottom: 16 }}
            columns={[
              {
                title: t("product"),
                dataIndex: "productName",
                render: (name, record) => (
                  <span>
                    {name}
                    {record.productColor && (
                      <span style={{ color: "#666", marginLeft: 4 }}>({record.productColor})</span>
                    )}
                  </span>
                ),
              },
              {
                title: t("code"),
                dataIndex: "productCode",
                render: (code, record) => {
                  if (code) return code;
                  const product = products.find((p) => p.id === record.productId);
                  return product?.code || "-";
                },
              },
              {
                title: t("quantity"),
                dataIndex: "quantity",
                render: (value, record) =>
                  editingInvoice ? (
                    <InputNumber
                      min={0}
                      value={value}
                      onChange={(v) => handleInvoiceLineChange(record._index, "quantity", v || 0)}
                      style={{ width: 80 }}
                    />
                  ) : (
                    (() => {
                      const originalQuantity = getOriginalQuantity(record._index);
                      const hasDiscrepancy = originalQuantity !== null && originalQuantity !== value;
                      return (
                        <div>
                          <div>{value}</div>
                          {hasDiscrepancy && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#ff4d4f",
                                marginTop: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <WarningOutlined style={{ fontSize: 10 }} />
                              <span>Origineel: {originalQuantity}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ),
              },
              {
                title: t("unit_price"),
                dataIndex: "unitPrice",
                render: (value, record) =>
                  editingInvoice ? (
                    <InputNumber
                      min={0}
                      step={0.01}
                      prefix={invoiceCurrency}
                      value={value}
                      onChange={(v) => handleInvoiceLineChange(record._index, "unitPrice", v || 0)}
                      style={{ width: 100 }}
                    />
                  ) : (
                    (() => {
                      const originalPrice = getOriginalUnitPrice(record._index);
                      const hasDiscrepancy = originalPrice !== null && Math.abs(originalPrice - (value || 0)) > 0.01;
                      return (
                        <div>
                          <div>{formatPriceByCurrency(value || 0, invoiceCurrency)}</div>
                          {hasDiscrepancy && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#ff4d4f",
                                marginTop: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <WarningOutlined style={{ fontSize: 10 }} />
                              <span>Origineel: {formatPriceByCurrency(originalPrice, invoiceCurrency)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ),
              },
              {
                title: "Korting",
                dataIndex: "discountPercentage",
                render: (value, record) =>
                  editingInvoice ? (
                    <InputNumber
                      min={0}
                      max={100}
                      value={value}
                      onChange={(v) => handleInvoiceLineChange(record._index, "discountPercentage", v || 0)}
                      style={{ width: 70 }}
                      suffix="%"
                    />
                  ) : value ? (
                    `${value}%`
                  ) : (
                    "-"
                  ),
              },
              {
                title: t("total"),
                dataIndex: "totalPrice",
                render: (v) => formatPriceByCurrency(v || 0, invoiceCurrency),
              },
              ...(editingInvoice
                ? [
                    {
                      title: "Acties",
                      dataIndex: "actions",
                      width: 80,
                      render: (_, record) => (
                        <Button
                          size="small"
                          type="link"
                          danger
                          onClick={() => handleRemoveInvoiceLine(record._index)}
                          style={{ padding: 0 }}
                        >
                          Verwijderen
                        </Button>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}

        {(editingInvoice ? editedAdditionalCosts : invoice.additionalCosts || []).length > 0 && (
          <>
            <h5 style={{ marginTop: 16 }}>Bijkomende kosten</h5>
            <Table
              // Zonder scroll.x loopt de tabel bij een smalle hoofdkolom buiten zijn kaart.
              scroll={{ x: 640 }}
              dataSource={(editingInvoice ? editedAdditionalCosts : invoice.additionalCosts || []).map((c, i) => ({
                ...c,
                key: i,
                _index: i,
              }))}
              pagination={false}
              size="small"
              style={{ marginBottom: 16 }}
              columns={[
                { title: "Omschrijving", dataIndex: "description" },
                { title: t("quantity"), dataIndex: "quantity", render: (v) => v || 1 },
                {
                  title: t("unit_price"),
                  dataIndex: "unitPrice",
                  render: (v) => (v ? formatPriceByCurrency(v, invoiceCurrency) : "-"),
                },
                {
                  title: t("total"),
                  dataIndex: "amount",
                  render: (value, record) =>
                    editingInvoice ? (
                      <InputNumber
                        min={0}
                        step={0.01}
                        prefix={invoiceCurrency}
                        value={value}
                        onChange={(v) => handleAdditionalCostChange(record._index, "amount", v || 0)}
                        style={{ width: 100 }}
                      />
                    ) : (
                      formatPriceByCurrency(value || 0, invoiceCurrency)
                    ),
                },
              ]}
            />
          </>
        )}

        {(editingInvoice ? editedDiscountLines : invoice.discountLines || []).length > 0 && (
          <>
            <h5 style={{ marginTop: 16 }}>Kortingsregels</h5>
            <Table
              // Zonder scroll.x loopt de tabel bij een smalle hoofdkolom buiten zijn kaart.
              scroll={{ x: 640 }}
              dataSource={(editingInvoice ? editedDiscountLines : invoice.discountLines || []).map((d, i) => ({
                ...d,
                key: d.id || i,
              }))}
              pagination={false}
              size="small"
              style={{ marginBottom: 16 }}
              columns={[
                {
                  title: "Omschrijving",
                  dataIndex: "description",
                  render: (value, record) =>
                    editingInvoice ? (
                      <Input
                        value={value}
                        placeholder="bv. Volumekorting"
                        onChange={(e) => handleDiscountLineChange(record.id, "description", e.target.value)}
                      />
                    ) : (
                      value || "-"
                    ),
                },
                {
                  title: "Bedrag",
                  dataIndex: "amount",
                  width: 200,
                  render: (value, record) =>
                    editingInvoice ? (
                      <InputNumber
                        min={0}
                        step={0.01}
                        prefix={invoiceCurrency}
                        value={Math.abs(value)}
                        onChange={(v) =>
                          handleDiscountLineChange(
                            record.id,
                            "amount",
                            v === null || v === undefined ? 0 : -Math.abs(v),
                          )
                        }
                        style={{ width: 150 }}
                      />
                    ) : (
                      <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                        {formatPriceByCurrency(value || 0, invoiceCurrency)}
                      </span>
                    ),
                },
                ...(editingInvoice
                  ? [
                      {
                        title: "Acties",
                        dataIndex: "actions",
                        width: 80,
                        render: (_, record) => (
                          <Button
                            size="small"
                            type="link"
                            danger
                            onClick={() => handleRemoveDiscountLine(record.id)}
                            style={{ padding: 0 }}
                          >
                            Verwijderen
                          </Button>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </>
        )}

        {/* Optelling onder de regels, zoals in het ontwerp. Deze bedragen stonden
            alleen in de zijkolom, los van de regels waar ze uit voortkomen. */}
        {(editingInvoice ? editedInvoiceLines : invoice.lines || []).length > 0 && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 12,
              borderTop: "1px solid rgba(5, 5, 5, 0.06)",
              display: "flex",
              flexDirection: "column",
              gap: 6,
              alignItems: "flex-end",
            }}
          >
            {[
              { label: "Subtotaal producten", value: invoice.subtotalProducts || 0 },
              { label: "Bijkomende kosten", value: invoice.subtotalAdditionalCosts || 0 },
              ...(invoice.discountLines?.length > 0 || (invoice.subtotalDiscounts || 0) !== 0
                ? [{ label: "Kortingen", value: invoice.subtotalDiscounts || 0, negative: true }]
                : []),
            ].map((row) => (
              <div key={row.label} style={{ display: "flex", gap: 24, alignItems: "baseline" }}>
                <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.55)" }}>{row.label}</span>
                <Num style={{ minWidth: 120, textAlign: "right", color: row.negative ? "#9E2F2F" : undefined }}>
                  {formatPriceByCurrency(row.value, invoiceCurrency)}
                </Num>
              </div>
            ))}
            <div
              style={{
                display: "flex",
                gap: 24,
                alignItems: "baseline",
                paddingTop: 6,
                borderTop: "1px solid rgba(5, 5, 5, 0.06)",
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 500 }}>Totaalbedrag</span>
              <Num strong size={15} style={{ minWidth: 120, textAlign: "right" }}>
                {formatPriceByCurrency(invoice.totalAmount || 0, invoiceCurrency)}
              </Num>
            </div>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <Space wrap>
            {invoice.pdfUrl && (
              <a href={invoice.pdfUrl} target="_blank" rel="noopener noreferrer">
                <Button type="primary" size="small">
                  Download factuur PDF
                </Button>
              </a>
            )}
            <Upload
              accept=".pdf"
              showUploadList={false}
              beforeUpload={(file) => {
                handleUploadInvoicePdf(file);
                return false;
              }}
              disabled={uploadingInvoicePdf}
            >
              <Button icon={<UploadOutlined />} size="small" loading={uploadingInvoicePdf}>
                {invoice.pdfUrl ? "Nieuwe PDF uploaden" : "PDF uploaden"}
              </Button>
            </Upload>
            {invoice.pdfUrl && (
              <Popconfirm
                title="Factuur PDF verwijderen"
                description="Weet je zeker dat je de geüploade factuur PDF wilt verwijderen?"
                onConfirm={handleDeleteInvoicePdf}
                okText="Verwijderen"
                cancelText="Annuleren"
                okButtonProps={{ loading: uploadingInvoicePdf }}
              >
                <Button danger size="small" loading={uploadingInvoicePdf}>
                  Verwijderen
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>

        {invoice.remarks && (
          <div style={{ marginTop: 12, padding: 8, backgroundColor: "#fff", borderRadius: 4 }}>
            <strong>Opmerkingen:</strong>
            <div style={{ marginTop: 4, color: "#666" }}>{invoice.remarks}</div>
          </div>
        )}
      </Card>

      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Space>
              <span style={{ fontWeight: "bold" }}>Betalingen</span>
              {totalsMismatch && (
                <Tooltip
                  title={
                    <div>
                      <div>
                        Som betalingen: <strong>{formatPriceByCurrency(paymentsSum, invoiceCurrency)}</strong>
                      </div>
                      <div>
                        Opgeslagen betaald: <strong>{formatPriceByCurrency(storedTotalPaid, invoiceCurrency)}</strong>
                      </div>
                      <div style={{ marginTop: 4, color: "#ffccc7" }}>
                        Verschil:{" "}
                        <strong>
                          {formatPriceByCurrency(Math.abs(paymentsSum - storedTotalPaid), invoiceCurrency)}
                        </strong>
                      </div>
                    </div>
                  }
                >
                  <Tag color="orange" style={{ marginLeft: 4, cursor: "help" }}>
                    <WarningOutlined style={{ marginRight: 4 }} />
                    Totalen komen niet overeen
                  </Tag>
                </Tooltip>
              )}
            </Space>
            <Space>
              {/*{totalsMismatch && (*/}
              {/*  <Button*/}
              {/*    icon={<ReloadOutlined />}*/}
              {/*    size="small"*/}
              {/*    danger*/}
              {/*    onClick={handleRecalculateTotals}*/}
              {/*    loading={recalculating}*/}
              {/*  >*/}
              {/*    Totalen herberekenen*/}
              {/*  </Button>*/}
              {/*)}*/}
              <Button
                icon={<PlusOutlined />}
                size="small"
                onClick={() => {
                  resetCorrectionForm();
                  setShowCorrectionModal(true);
                }}
              >
                Correctie toevoegen
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={() => {
                  resetPaymentForm();
                  setPaymentAmount(invoice.amountDue > 0 ? invoice.amountDue : null);
                  setShowPaymentModal(true);
                }}
              >
                Betaling toevoegen
              </Button>
            </Space>
          </div>
        }
      >
        {/* Payments Section */}
        <div>
          {allPayments.length === 0 ? (
            <div
              style={{
                padding: 16,
                backgroundColor: "#fafafa",
                borderRadius: 8,
                textAlign: "center",
                color: "#999",
              }}
            >
              Nog geen betalingen geregistreerd
            </div>
          ) : (
            <Table
              // Zonder scroll.x loopt de tabel bij een smalle hoofdkolom buiten zijn kaart.
              scroll={{ x: 560 }}
              dataSource={allPayments}
              // Stabiele sleutel: met `key: index` verspringt de uitklapstatus zodra
              // er een betaling bij komt of weggaat.
              rowKey={(payment) => payment.id || `${payment.invoiceId}-${payment.paymentDate}-${payment.amount}`}
              pagination={false}
              size="small"
              expandable={{
                // Bron, notities, bewijs en "door" stonden als vier eigen kolommen in
                // de rij terwijl ze meestal leeg of altijd hetzelfde zijn. Nu achter
                // de uitklap, net als op Inkooporder detail en Zending detail.
                rowExpandable: (payment) =>
                  Boolean(
                    payment._sourceProformaNumber ||
                      payment.isLegacy ||
                      payment.migratedFromLegacy ||
                      payment.notes ||
                      payment.proofUrl ||
                      payment.createdBy ||
                      payment.paidBy,
                  ),
                expandedRowRender: (payment) => renderPaymentDetails(payment),
              }}
              columns={[
                {
                  title: "Datum",
                  dataIndex: "paymentDate",
                  render: (v, record) => {
                    const date = v || record.paidAt || record.date;
                    return date ? dayjs.unix(date).format("DD-MM-YYYY") : "-";
                  },
                },
                {
                  title: "Bedrag",
                  dataIndex: "amount",
                  align: "right",
                  width: 170,
                  render: (v, record) => {
                    const isCorrection = record.type === "correction";
                    const isNegative = (v || 0) < 0;
                    const color = isCorrection ? (isNegative ? "#fa8c16" : "#13c2c2") : "#52c41a";

                    // De kolom "Type" is weg: het zijn bijna altijd betalingen. Maar
                    // niet altijd — handleAddCorrection schrijft type: "correction",
                    // en een debetcorrectie van -$500 mag niet als gewone betaling
                    // lezen. Daarom één tag, en alléén voor de uitzonderingen.
                    const kindTag = record.isLegacy ? (
                      <Tag color="default" style={{ marginInlineEnd: 0 }}>
                        Legacy
                      </Tag>
                    ) : isCorrection ? (
                      <Tag color={isNegative ? "orange" : "cyan"} style={{ marginInlineEnd: 0 }}>
                        {isNegative ? "Debetcorrectie" : "Creditcorrectie"}
                      </Tag>
                    ) : null;

                    // Same check as the invoice list's warning icon, but scoped to this one
                    // payment so the user can jump straight to the conflicting invoice
                    // instead of having to go hunt for it from the overview.
                    const dupMatches = findDuplicatePaymentMatches(record, supplierPayments, invoicesById);
                    const sameInvoiceDuplicate = dupMatches.some((m) => m.invoice.id === invoice.id);
                    const otherInvoiceMatches = [
                      ...new Map(
                        dupMatches.filter((m) => m.invoice.id !== invoice.id).map((m) => [m.invoice.id, m.invoice]),
                      ).values(),
                    ];

                    return (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {/* Duplicaatmelding was twee oranje tekstregels per match, wat de
                              rij hoog én breed maakte. Nu één icoon; de details staan in de
                              popover. Een kopieer-icoon leest als "dit bestaat twee keer",
                              een waarschuwingsdriehoek als "er is iets stuk". */}
                          {dupMatches.length > 0 && (
                            <Popover
                              trigger="click"
                              placement="bottomRight"
                              title="Mogelijk dubbele betaling"
                              content={
                                <div style={{ maxWidth: 280, display: "flex", flexDirection: "column", gap: 6 }}>
                                  <span style={{ fontSize: 12.5, color: "rgba(0, 0, 0, 0.55)" }}>
                                    Zelfde bedrag ({formatPriceByCurrency(record.amount, invoiceCurrency)}) rond dezelfde
                                    datum als een andere betaling van deze leverancier.
                                  </span>
                                  {sameInvoiceDuplicate && <span>Twee keer dezelfde betaling op deze factuur.</span>}
                                  {otherInvoiceMatches.map((dupInvoice) => (
                                    <RecordLink
                                      key={dupInvoice.id}
                                      to={`${urls.purchaseOrderInvoiceDetail}${dupInvoice.id}`}
                                    >
                                      Ook bij factuur {dupInvoice.invoiceNumber || dupInvoice.id}
                                    </RecordLink>
                                  ))}
                                </div>
                              }
                            >
                              <CopyOutlined
                                onClick={(e) => e.stopPropagation()}
                                style={{ color: "#fa8c16", fontSize: 13, cursor: "pointer", flex: "none" }}
                              />
                            </Popover>
                          )}
                          {kindTag}
                          <strong style={{ color }}>{formatPriceByCurrency(v || 0, invoiceCurrency)}</strong>
                        </div>
                        {record.exchangeRate && (
                          <Num size={11} muted>
                            €{(v * record.exchangeRate).toFixed(2)} @ {record.exchangeRate}
                          </Num>
                        )}
                      </div>
                    );
                  },
                },
                {
                  title: "Referentie",
                  dataIndex: "reference",
                  render: (v, record) => {
                    if (record.isLegacy) {
                      return record.sourceOrderNumber || "-";
                    }
                    return v || "-";
                  },
                },
                {
                  title: "Acties",
                  dataIndex: "actions",
                  render: (_, record) => {
                    if (record._sourceProformaNumber) {
                      return (
                        <Button
                          size="small"
                          type="link"
                          style={{ padding: 0 }}
                          onClick={() => navigate(`${urls.purchaseOrderInvoiceDetail}${record.invoiceId}`)}
                        >
                          Bekijk proforma
                        </Button>
                      );
                    }
                    return (
                      <Space size={2} direction="vertical">
                        {!record.isLegacy && (
                          <Button
                            size="small"
                            type="link"
                            onClick={() => handleEditPayment(record)}
                            style={{ padding: 0 }}
                          >
                            Bewerken
                          </Button>
                        )}
                        <Popconfirm
                          title="Betaling verwijderen?"
                          description={`${formatPriceByCurrency(record.amount, invoiceCurrency)}${record.isLegacy ? " (legacy uit inkooporder)" : ""} wordt verwijderd en de factuur wordt herberekend.`}
                          onConfirm={() => handleDeletePayment(record)}
                          okText="Verwijderen"
                          cancelText="Annuleren"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            size="small"
                            type="link"
                            danger
                            loading={deletingPaymentId === record.id}
                            style={{ padding: 0 }}
                          >
                            Verwijderen
                          </Button>
                        </Popconfirm>
                      </Space>
                    );
                  },
                },
              ]}
            />
          )}

          {/* Gewogen koers over alle betalingen. Deze berekening bestond al — de
              ontvangst-flow op Zending detail gebruikt hem om kostprijzen om te
              rekenen — maar was hier nergens zichtbaar. Betalingen zonder eigen
              koers vallen buiten de weging; dat staat er nu expliciet bij, want
              anders lijkt het bedrag onverklaarbaar. */}
          {weightedExchangeRate !== null && (
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: "1px solid rgba(5, 5, 5, 0.06)",
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                alignItems: "baseline",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.55)" }}>
                Gewogen koers over alle betalingen
                {paymentsWithoutRate > 0 && (
                  <span style={{ color: "#96601A" }}>
                    {" "}
                    — {paymentsWithoutRate} betaling{paymentsWithoutRate === 1 ? "" : "en"} zonder koers telt niet mee
                  </span>
                )}
              </span>
              <Num strong>1 {invoiceCurrency} = {weightedExchangeRate.toFixed(4)} EUR</Num>
            </div>
          )}
        </div>
      </Card>

      {invoice.type === "final" && (
        <Card
          style={{ marginTop: 16 }}
          title={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: "bold" }}>Gekoppelde proforma's</span>
              {!editingProformaLinks ? (
                <Button size="small" onClick={handleStartEditingProformaLinks}>
                  Bewerken
                </Button>
              ) : (
                <Space>
                  <Button size="small" onClick={handleCancelEditingProformaLinks}>
                    Annuleren
                  </Button>
                  <Button size="small" type="primary" onClick={handleSaveProformaLinks} loading={savingProformaLinks}>
                    Opslaan
                  </Button>
                </Space>
              )}
            </div>
          }
        >
          {proformaLinkIssues.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="De koppeling met een pro forma klopt niet"
              description={
                <>
                  <ul style={{ paddingLeft: 18, margin: 0 }}>
                    {proformaLinkIssues.map((text, i) => (
                      <li key={i}>{text}</li>
                    ))}
                  </ul>
                  <div style={{ marginTop: 6 }}>
                    Klik op Bewerken en Opslaan om een pro forma die hier hoort te herstellen, of vink hem
                    uit als hij bij een andere factuur hoort.
                  </div>
                </>
              }
            />
          )}
          {!editingProformaLinks ? (
            linkedProformaInvoices.length > 0 ? (
              <Table
                // Zonder scroll.x loopt de tabel bij een smalle hoofdkolom buiten zijn kaart.
                scroll={{ x: 640 }}
                dataSource={linkedProformaInvoices.map((inv) => ({ ...inv, key: inv.id }))}
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Factuurnummer",
                    dataIndex: "invoiceNumber",
                    render: (v, record) => (
                      <Tag
                        color="orange"
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`${urls.purchaseOrderInvoiceDetail}${record.id}`)}
                      >
                        {v}
                      </Tag>
                    ),
                  },
                  { title: "Order", dataIndex: "purchaseOrderNumber" },
                  {
                    title: "Aanbetalingsbedrag",
                    dataIndex: "totalAmount",
                    render: (v, record) => formatPriceByCurrency(v || 0, record.currency || invoiceCurrency),
                  },
                  {
                    title: "Datum van verrekening",
                    dataIndex: "linkedAt",
                    render: (v, record) =>
                      record.linkedFinalInvoiceId !== invoice.id ? (
                        <Tag color="red">koppeling klopt niet</Tag>
                      ) : v ? (
                        dayjs.unix(v).format("DD-MM-YYYY")
                      ) : (
                        "-"
                      ),
                  },
                ]}
              />
            ) : (
              <div style={{ color: "#999" }}>Geen proforma's gekoppeld aan deze factuur.</div>
            )
          ) : candidateProformaInvoices.length > 0 ? (
            <Checkbox.Group
              style={{ width: "100%" }}
              value={editedLinkedProformaIds}
              onChange={(values) => setEditedLinkedProformaIds(values)}
            >
              <Space direction="vertical">
                {candidateProformaInvoices.map((inv) => {
                  const alreadyLinkedElsewhere = inv.linkedFinalInvoiceId && inv.linkedFinalInvoiceId !== invoice.id;
                  const currencyMismatch = (inv.currency || invoiceCurrency) !== invoiceCurrency;
                  // Staat hij al in de lijst van deze factuur, dan moet je hem altijd kunnen uitvinken.
                  const inSavedList = (invoice.linkedProformaInvoiceIds || []).includes(inv.id);
                  const otherFinalNumber = alreadyLinkedElsewhere
                    ? invoicesById.get(inv.linkedFinalInvoiceId)?.invoiceNumber || inv.linkedFinalInvoiceId
                    : null;
                  return (
                    <Checkbox
                      key={inv.id}
                      value={inv.id}
                      disabled={!inSavedList && (alreadyLinkedElsewhere || currencyMismatch)}
                    >
                      {inv.invoiceNumber} ({inv.poNumber}) —{" "}
                      {formatPriceByCurrency(inv.totalAmount || 0, inv.currency || invoiceCurrency)}
                      {alreadyLinkedElsewhere && (
                        <span style={{ color: inSavedList ? "#cf1322" : "#faad14", marginLeft: 8 }}>
                          {inSavedList
                            ? `verrekend met ${otherFinalNumber} - vink uit om hem hier los te halen`
                            : `al gekoppeld aan ${otherFinalNumber}`}
                        </span>
                      )}
                      {currencyMismatch && !inSavedList && (
                        <span style={{ color: "#ff4d4f", marginLeft: 8 }}>
                          valuta komt niet overeen ({inv.currency} vs {invoiceCurrency}) — kan niet gekoppeld worden
                        </span>
                      )}
                    </Checkbox>
                  );
                })}
              </Space>
            </Checkbox.Group>
          ) : (
            <div style={{ color: "#999" }}>Geen proforma's van deze leverancier beschikbaar om te koppelen.</div>
          )}
        </Card>
      )}

      {/* Geschiedenis (history subcollection) */}
      <Card size="small">
        <Collapse>
          <Panel
            header={
              <span>
                Geschiedenis{" "}
                {!historyLoading && invoiceHistory.length > 0 && (
                  <Tag style={{ marginLeft: 4 }}>{invoiceHistory.length}</Tag>
                )}
              </span>
            }
            key="history"
          >
            {historyLoading ? (
              <div style={{ textAlign: "center", padding: 24 }}>
                <Spin />
              </div>
            ) : invoiceHistory.length === 0 ? (
              <div style={{ color: "#999", padding: 8 }}>Nog geen geschiedenis beschikbaar.</div>
            ) : (
              <div style={{ maxHeight: 500, overflowY: "auto", paddingTop: 8 }}>
                <Timeline>
                  {invoiceHistory.map((entry) => (
                    <Timeline.Item key={entry.id}>
                      <div style={{ marginBottom: 4 }}>
                        <span style={{ fontWeight: 500 }}>{entry.changedBy}</span>
                        <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>
                          {dayjs.unix(entry.changedAt).format("DD-MM-YYYY HH:mm")}
                        </span>
                      </div>
                      {entry.changes?.map((change, idx) => {
                        const action = change.action || change.field;
                        if (action === "invoice_created") {
                          return (
                            <div key={idx} style={{ fontSize: 13, color: "#52c41a" }}>
                              ✓ Factuur aangemaakt
                            </div>
                          );
                        }
                        if (action === "status_change" || change.field === "status") {
                          return (
                            <div key={idx} style={{ fontSize: 13, marginBottom: 6 }}>
                              Status: <Tag>{invoiceStatusLabels[change.oldValue] || change.oldValue}</Tag>
                              <ArrowRightOutlined style={{ marginRight: 8 }} />
                              <Tag color="blue">{invoiceStatusLabels[change.newValue] || change.newValue}</Tag>
                            </div>
                          );
                        }
                        if (action === "payment_added" || change.field === "payment_added") {
                          return (
                            <div key={idx} style={{ fontSize: 13, color: "#1890ff" }}>
                              + Betaling toegevoegd
                              {change.metadata && (
                                <span style={{ color: "#555", marginLeft: 6 }}>{change.metadata}</span>
                              )}
                            </div>
                          );
                        }
                        if (action === "payment_updated" || change.field === "payment_updated") {
                          return (
                            <div key={idx} style={{ fontSize: 13 }}>
                              Betaling bijgewerkt
                              {change.metadata && (
                                <span style={{ color: "#555", marginLeft: 6 }}>{change.metadata}</span>
                              )}
                            </div>
                          );
                        }
                        if (action === "correction_added" || change.field === "correction_added") {
                          return (
                            <div key={idx} style={{ fontSize: 13, color: "#13c2c2" }}>
                              ± Correctie geboekt
                              {change.metadata && (
                                <span style={{ color: "#555", marginLeft: 6 }}>{change.metadata}</span>
                              )}
                            </div>
                          );
                        }
                        if (action === "payment_deleted" || change.field === "payment_deleted") {
                          return (
                            <div key={idx} style={{ fontSize: 13, color: "#ff4d4f" }}>
                              − Betaling verwijderd
                              {change.metadata && (
                                <span style={{ color: "#555", marginLeft: 6 }}>{change.metadata}</span>
                              )}
                            </div>
                          );
                        }
                        if (action === "totals_recalculated" || change.field === "totals_recalculated") {
                          return (
                            <div key={idx} style={{ fontSize: 13, color: "#722ed1" }}>
                              ↻ Totalen herberekend
                              {change.metadata && (
                                <span style={{ color: "#555", marginLeft: 6 }}>{change.metadata}</span>
                              )}
                            </div>
                          );
                        }
                        if (action === "invoice_edit" || change.action === "invoice_edit") {
                          const iconMap = {
                            line_added: { color: "#52c41a", prefix: "+" },
                            line_removed: { color: "#ff4d4f", prefix: "−" },
                            line_quantity: { color: "#1890ff", prefix: "~" },
                            line_price: { color: "#1890ff", prefix: "~" },
                            line_discount: { color: "#faad14", prefix: "~" },
                            cost_added: { color: "#52c41a", prefix: "+" },
                            cost_removed: { color: "#ff4d4f", prefix: "−" },
                            cost_changed: { color: "#1890ff", prefix: "~" },
                            discount_added: { color: "#52c41a", prefix: "+" },
                            discount_removed: { color: "#ff4d4f", prefix: "−" },
                            discount_changed: { color: "#1890ff", prefix: "~" },
                            total_amount: { color: "#888", prefix: "Σ" },
                            proforma_linked: { color: "#52c41a", prefix: "+" },
                            proforma_unlinked: { color: "#ff4d4f", prefix: "−" },
                          };
                          const style = iconMap[change.field] || { color: "#555", prefix: "~" };
                          return (
                            <div key={idx} style={{ fontSize: 13, color: style.color, marginBottom: 2 }}>
                              <span style={{ marginRight: 5, fontWeight: "bold" }}>{style.prefix}</span>
                              {change.metadata || change.field}
                            </div>
                          );
                        }
                        return (
                          <div key={idx} style={{ fontSize: 12, color: "#888" }}>
                            {change.field}
                            {change.oldValue != null && (
                              <>
                                : {JSON.stringify(change.oldValue)} <ArrowRightOutlined style={{ marginRight: 8 }} />{" "}
                                {JSON.stringify(change.newValue)}
                              </>
                            )}
                            {change.oldValue == null && change.newValue != null && (
                              <span>: {JSON.stringify(change.newValue)}</span>
                            )}
                          </div>
                        );
                      })}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </div>
            )}
          </Panel>
        </Collapse>
      </Card>
      </DetailLayout>

      {/* Add Payment Modal */}
      <Modal
        title="Betaling toevoegen"
        open={showPaymentModal}
        onOk={handleAddPayment}
        onCancel={() => {
          setShowPaymentModal(false);
          resetPaymentForm();
        }}
        okText="Toevoegen"
        cancelText="Annuleren"
        confirmLoading={savingPayment}
        okButtonProps={{ disabled: uploadingPaymentProof }}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bedrag ({invoiceCurrency}):</label>
            <InputNumber
              min={0}
              step={0.01}
              value={paymentAmount}
              onChange={setPaymentAmount}
              style={{ width: "100%" }}
              prefix={invoiceCurrency}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Datum:</label>
            <DatePicker value={paymentDate} onChange={setPaymentDate} style={{ width: "100%" }} format="DD-MM-YYYY" />
          </div>
          {duplicatePaymentMatches.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Mogelijk dubbele betaling"
              description={
                <span>
                  Er staat al een betaling van {formatPriceByCurrency(paymentAmount, invoiceCurrency)} rond deze datum
                  bij: {duplicatePaymentMatches.map((m) => m.invoice.invoiceNumber || m.invoice.id).join(", ")}.
                </span>
              }
            />
          )}
          {invoiceCurrency !== "EUR" && (
            <div>
              <label style={{ display: "block", marginBottom: 4 }}>Wisselkoers (naar EUR):</label>
              <InputNumber
                min={0}
                step={0.0001}
                value={paymentExchangeRate}
                onChange={setPaymentExchangeRate}
                style={{ width: "100%" }}
                placeholder="Bijv. 0.92"
              />
              {!paymentExchangeRate && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 8 }}
                  message="Zonder wisselkoers telt deze betaling niet mee in de gewogen-koers-berekening bij het verwerken van de ontvangst."
                />
              )}
            </div>
          )}
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Referentie:</label>
            <Input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Notities:</label>
            <TextArea value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} rows={3} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bewijs (PDF/afbeelding):</label>
            <Upload
              beforeUpload={(file) => {
                handleUploadPaymentProof(file);
                return false;
              }}
              onRemove={() => setPaymentProofFile(null)}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Upload bewijs</Button>
            </Upload>
          </div>
        </Space>
      </Modal>

      {/* Edit Payment Modal */}
      <Modal
        title="Betaling bewerken"
        open={showEditPaymentModal}
        onOk={handleUpdatePayment}
        onCancel={() => {
          setShowEditPaymentModal(false);
          setEditingPayment(null);
          resetPaymentForm();
        }}
        okText="Opslaan"
        cancelText="Annuleren"
        confirmLoading={savingPayment}
        okButtonProps={{ disabled: uploadingPaymentProof }}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bedrag ({invoiceCurrency}):</label>
            <InputNumber
              min={0}
              step={0.01}
              value={paymentAmount}
              onChange={setPaymentAmount}
              style={{ width: "100%" }}
              prefix={invoiceCurrency}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Datum:</label>
            <DatePicker value={paymentDate} onChange={setPaymentDate} style={{ width: "100%" }} format="DD-MM-YYYY" />
          </div>
          {duplicatePaymentMatches.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Mogelijk dubbele betaling"
              description={
                <span>
                  Er staat al een betaling van {formatPriceByCurrency(paymentAmount, invoiceCurrency)} rond deze datum
                  bij: {duplicatePaymentMatches.map((m) => m.invoice.invoiceNumber || m.invoice.id).join(", ")}.
                </span>
              }
            />
          )}
          {invoiceCurrency !== "EUR" && (
            <div>
              <label style={{ display: "block", marginBottom: 4 }}>Wisselkoers (naar EUR):</label>
              <InputNumber
                min={0}
                step={0.0001}
                value={paymentExchangeRate}
                onChange={setPaymentExchangeRate}
                style={{ width: "100%" }}
                placeholder="Bijv. 0.92"
              />
              {!paymentExchangeRate && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 8 }}
                  message="Zonder wisselkoers telt deze betaling niet mee in de gewogen-koers-berekening bij het verwerken van de ontvangst."
                />
              )}
            </div>
          )}
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Referentie:</label>
            <Input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Notities:</label>
            <TextArea value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} rows={3} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bewijs (PDF/afbeelding):</label>
            <Upload
              beforeUpload={(file) => {
                handleUploadPaymentProof(file);
                return false;
              }}
              onRemove={() => setPaymentProofFile(null)}
              maxCount={1}
              defaultFileList={
                editingPayment?.proofUrl
                  ? [{ uid: "1", name: "Bewijs", status: "done", url: editingPayment.proofUrl }]
                  : []
              }
            >
              <Button icon={<UploadOutlined />}>Upload bewijs</Button>
            </Upload>
          </div>
        </Space>
      </Modal>

      {/* Correction Modal */}
      <Modal
        title="Correctie toevoegen"
        open={showCorrectionModal}
        onOk={handleAddCorrection}
        onCancel={() => {
          setShowCorrectionModal(false);
          resetCorrectionForm();
        }}
        okText="Toevoegen"
        cancelText="Annuleren"
        confirmLoading={savingCorrection}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div
            style={{
              padding: "8px 12px",
              backgroundColor: "#fffbe6",
              border: "1px solid #ffe58f",
              borderRadius: 6,
              fontSize: 13,
              color: "#ad6800",
            }}
          >
            Gebruik correcties om historische betalingsverschillen recht te trekken. Een{" "}
            <strong>creditcorrectie</strong> verhoogt het betaalde bedrag (bijv. een betaling die elders geboekt staat
            maar voor deze factuur was). Een <strong>debetcorrectie</strong> verlaagt het betaalde bedrag (bijv. een
            deel van een betaling op deze factuur die eigenlijk voor een andere factuur was).
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Type correctie:</label>
            <Select
              value={correctionIsNegative ? "debet" : "credit"}
              onChange={(v) => setCorrectionIsNegative(v === "debet")}
              style={{ width: "100%" }}
              options={[
                { value: "credit", label: "Creditcorrectie (+) — verhoogt het betaalde bedrag" },
                { value: "debet", label: "Debetcorrectie (−) — verlaagt het betaalde bedrag" },
              ]}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bedrag ({invoiceCurrency}):</label>
            <InputNumber
              min={0}
              step={0.01}
              value={correctionAmount}
              onChange={setCorrectionAmount}
              style={{ width: "100%" }}
              prefix={correctionIsNegative ? `−${invoiceCurrency}` : `+${invoiceCurrency}`}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Datum:</label>
            <DatePicker
              value={correctionDate}
              onChange={setCorrectionDate}
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>
              Reden <span style={{ color: "#ff4d4f" }}>*</span>:
            </label>
            <TextArea
              value={correctionReason}
              onChange={(e) => setCorrectionReason(e.target.value)}
              rows={3}
              placeholder="Bijv: Deel van betaling PO-2024-015 betrof deze factuur"
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Referentie (optioneel):</label>
            <Input
              value={correctionReference}
              onChange={(e) => setCorrectionReference(e.target.value)}
              placeholder="Bijv: PO-2024-015 of bankreferentie"
            />
          </div>
          {correctionAmount > 0 && (
            <div
              style={{
                padding: "8px 12px",
                backgroundColor: correctionIsNegative ? "#fff7e6" : "#f6ffed",
                border: `1px solid ${correctionIsNegative ? "#ffd591" : "#b7eb8f"}`,
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              Na correctie:{" "}
              <strong>
                {formatPriceByCurrency(
                  (invoice.totalPaid || 0) +
                    (correctionIsNegative ? -Math.abs(correctionAmount) : Math.abs(correctionAmount)),
                  invoiceCurrency,
                )}
              </strong>{" "}
              betaald, openstaand:{" "}
              <strong>
                {formatPriceByCurrency(
                  invoice.totalAmount -
                    ((invoice.totalPaid || 0) +
                      (correctionIsNegative ? -Math.abs(correctionAmount) : Math.abs(correctionAmount))),
                  invoiceCurrency,
                )}
              </strong>
            </div>
          )}
        </Space>
      </Modal>

      {/* Add Line Modal */}
      <Modal
        title="Factuurregel toevoegen"
        open={showAddLineModal}
        onOk={handleAddInvoiceLine}
        onCancel={handleCloseAddLineModal}
        okText="Toevoegen"
        cancelText="Annuleren"
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Product:</label>
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder="Selecteer een product"
              value={selectedProduct?.id}
              onChange={handleProductSelect}
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              options={products.map((p) => ({
                value: p.id,
                label: `${p.name}${p.color ? ` (${p.color})` : ""} - ${p.code || ""}`,
              }))}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Aantal:</label>
            <InputNumber min={0} value={newLineQuantity} onChange={setNewLineQuantity} style={{ width: "100%" }} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Prijs per stuk ({invoiceCurrency}):</label>
            <InputNumber
              min={0}
              step={0.01}
              value={newLineUnitPrice}
              onChange={setNewLineUnitPrice}
              style={{ width: "100%" }}
              prefix={invoiceCurrency}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Korting (%):</label>
            <InputNumber
              min={0}
              max={100}
              value={newLineDiscount}
              onChange={setNewLineDiscount}
              style={{ width: "100%" }}
              suffix="%"
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default PurchaseOrderInvoiceDetail;
